import { PlusOutlined, UnorderedListOutlined } from '@ant-design/icons';
import {
  Button,
  Card,
  Col as ColAnt,
  Form,
  Grid,
  Input,
  InputNumber,
  Row,
  Select,
  Switch,
  message
} from 'antd';
import React, { Fragment } from 'react';
import styled from 'styled-components';
import { relaciones, validarCaracterEspecial } from '../../utils';

const { Option } = Select;
const { useBreakpoint } = Grid;

const BeneficiariosList = ({ defaultList, onBack, onNext }) => {
  const [form] = Form.useForm();
  const screens = useBreakpoint();

  const onFinish = (values) => {
    if (values.list.length === 0) {
      message.warning('Definí un beneficiario');
      return;
    }
    if (verificarSumas()) {
      message.warning('Verificá el porcentaje total (100%)');
      return;
    }
    if (onNext) {
      onNext(values);
    }
  };

  const verificarSumas = () => {
    const reducer = (accumulator, currentValue) =>
      accumulator +
      (currentValue && currentValue.beneficio ? currentValue.beneficio : 0);
    const total = form.getFieldValue('list').reduce(reducer, 0);
    return total !== 100;
  };

  const goBack = () => {
    const { getFieldsValue } = form;
    if (onBack) {
      onBack(getFieldsValue());
    }
  };

  const Col = !screens.md
    ? ColAnt
    : styled((props) => (
        <ColAnt
          {...props}
          style={{
            ...props.style,
            paddingTop: 16,
            paddingBottom: 16,
            paddingLeft: 16,
            paddingRight: 16
          }}
        />
      ))`
        border-right: 1px solid #f0f0f0;
        border-bottom: 1px solid #f0f0f0;
        transition: background 0.3s;
        padding-top: 16px;
        padding-bottom: 16px;
        padding-left: 16px;
        padding-right: 16px;
        &:first-child {
          border-left: 1px solid #f0f0f0;
        }
        &:hover {
          background: #fafafa;
        }
      `;

  const BeneficiarioForm = ({ field }) => {
    const { setFieldsValue } = form;
    return (
      <>
        <Col
          xs={24}
          md={9}
          align="start"
          style={screens.md ? { borderLeft: '1px solid #f0f0f0' } : null}
        >
          <Form.Item
            {...field}
            name={[field.name, 'nombres']}
            label={screens.md ? '' : 'Nombres y Apellidos'}
            fieldKey={[field.fieldKey, 'nombres']}
            rules={[{ required: true, message: 'Campo requerido' }]}
            onKeyPress={(e) => {
              const pressedKey = String.fromCharCode(
                !e.charCode ? e.which : e.charCode
              );
              validarCaracterEspecial(e, pressedKey);
            }}
            onPaste={(e) => {
              const pastedText = e.clipboardData.getData('text');
              validarCaracterEspecial(e, pastedText);
            }}
          >
            <Input placeholder="Nombres y Apellidos" />
          </Form.Item>
        </Col>
        <Col xs={24} md={5} align="start">
          <Form.Item noStyle shouldUpdate>
            {({ getFieldValue }) =>
              getFieldValue('list')[field.name]?.relacion === 'otro' ? (
                <Form.Item
                  {...field}
                  label={screens.md ? '' : 'Relación'}
                  name={[field.name, 'relacionOtro']}
                  fieldKey={[field.fieldKey, 'relacionOtro']}
                  rules={[{ required: true, message: 'Campo requerido' }]}
                >
                  <Input
                    placeholder="Escribir..."
                    addonAfter={
                      <UnorderedListOutlined
                        onClick={() => {
                          let list = getFieldValue('list');
                          list = list.map((item, index) => {
                            return index === field.name
                              ? { ...item, relacion: undefined }
                              : item;
                          });
                          setFieldsValue({ list });
                        }}
                      />
                    }
                  />
                </Form.Item>
              ) : (
                <>
                  <Form.Item
                    {...field}
                    label={screens.md ? '' : 'Relación'}
                    name={[field.name, 'relacion']}
                    fieldKey={[field.fieldKey, 'relacion']}
                    rules={[{ required: true, message: 'Campo requerido' }]}
                  >
                    <Select placeholder="Elegir">
                      {relaciones.map((item) => (
                        <Option key={item.value} value={item.value}>
                          {item.label}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </>
              )
            }
          </Form.Item>
        </Col>
        <Col xs={24} md={3} style={{ textAlign: 'start' }}>
          <Form.Item
            {...field}
            label={screens.md ? '' : 'Porcentaje'}
            name={[field.name, 'beneficio']}
            fieldKey={[field.fieldKey, 'beneficio']}
            dependencies={[field.fieldKey, 'beneficio']}
            rules={[{ required: true, message: 'Campo requerido' }]}
          >
            <InputNumber
              min={0.1}
              max={100}
              // step={0.1}
              precision={1}
              formatter={(value) => `${value}%`}
              parser={(value) => {
                const valueNumber = parseFloat(value.replace('%', ''));
                return Number.isNaN(valueNumber) ? undefined : valueNumber;
              }}
              onKeyPress={(e) => {
                // Allow only one decimal point
                const { value } = e.target;
                if (e.key === '.' && value.includes('.')) {
                  e.preventDefault();
                }
              }}
            />
          </Form.Item>
        </Col>
        <Col
          xs={24}
          md={4}
          style={{ textAlign: screens.md ? 'center' : 'start' }}
        >
          <Form.Item noStyle shouldUpdate>
            {({ getFieldValue }) =>
              ['hijo', 'hermano', 'otro'].includes(
                getFieldValue('list')[field.name]?.relacion
              ) && (
                <Form.Item
                  {...field}
                  name={[field.name, 'mayor']}
                  label={screens.md ? '' : '¿Es mayor de edad?'}
                  fieldKey={[field.fieldKey, 'mayor']}
                  valuePropName="checked"
                  rules={[
                    {
                      validator: (_, checked) => {
                        if (!checked) {
                          // eslint-disable-next-line prefer-promise-reject-errors
                          return Promise.reject('Debe ser mayor');
                        }

                        return Promise.resolve();
                      }
                    }
                  ]}
                >
                  <Switch checkedChildren="Si" unCheckedChildren="No" />
                </Form.Item>
              )
            }
          </Form.Item>
        </Col>
      </>
    );
  };

  const headerStyle = {
    color: 'rgba(0, 0, 0, 0.85)',
    fontWeight: 500,
    textAlign: 'left',
    background: '#fafafa',
    borderTop: '1px solid #f0f0f0',
    borderBottom: '1px solid #f0f0f0',
    borderRight: '1px solid #f0f0f0'
  };

  if (!defaultList.list || defaultList.list.length === 0) {
    return null;
  }

  return (
    <>
      <Form
        name="beneficiariosList"
        initialValues={defaultList}
        onFinish={onFinish}
        form={form}
        layout="vertical"
        autoComplete="off"
      >
        <Form.List name="list">
          {(fields, { add, remove }) => {
            return (
              <>
                {screens.md && (
                  <Row
                    gutter={[16, 0]}
                    style={{
                      maxWidth: '1200px',
                      marginTop: 'auto',
                      marginBottom: 'auto',
                      marginLeft: 'auto',
                      marginRight: 'auto'
                    }}
                  >
                    <Col xs={24} md={9} align="start" style={headerStyle}>
                      Nombres y Apellidos
                    </Col>
                    <Col xs={16} md={5} align="start" style={headerStyle}>
                      Relación de parentezco
                    </Col>
                    <Col xs={8} md={3} align="start" style={headerStyle}>
                      % Porcentaje
                    </Col>
                    <Col
                      xs={8}
                      md={4}
                      align="center"
                      style={{ ...headerStyle, textAlign: 'center' }}
                    >
                      ¿Es mayor de edad?
                    </Col>
                    <Col xs={24} md={3} align="start" style={headerStyle} />
                    {fields.map((field) => (
                      <Fragment key={field.key}>
                        <BeneficiarioForm field={field} />
                        <Col xs={24} md={3} align="start">
                          <Button
                            type="dashed"
                            danger
                            onClick={() => {
                              remove(field.name);
                            }}
                          >
                            Borrar
                          </Button>
                        </Col>
                      </Fragment>
                    ))}
                    <ColAnt xs={24} style={{ paddingTop: 20 }}>
                      <Form.Item>
                        <Button
                          type="primary"
                          shape="round"
                          onClick={() => {
                            add();
                          }}
                        >
                          <PlusOutlined /> Agregar beneficiario adicional
                        </Button>
                      </Form.Item>
                    </ColAnt>
                  </Row>
                )}
                {screens.md === false && (
                  <>
                    {fields.map((field) => (
                      <Card
                        key={field.key}
                        bodyStyle={{ paddingBottom: 8 }}
                        style={{ marginBottom: 16 }}
                        actions={[
                          <Button
                            type="dashed"
                            danger
                            onClick={() => {
                              remove(field.name);
                            }}
                          >
                            Borrar
                          </Button>
                        ]}
                      >
                        <Row
                          gutter={[8, 4]}
                          style={{ maxWidth: '1200px', margin: 'auto' }}
                        >
                          <BeneficiarioForm field={field} />
                        </Row>
                      </Card>
                    ))}
                    <Col xs={24} style={{ paddingTop: 20 }}>
                      <Form.Item>
                        <Button
                          type="primary"
                          shape="round"
                          onClick={() => {
                            add();
                          }}
                        >
                          <PlusOutlined /> Agregar beneficiario adicional
                        </Button>
                      </Form.Item>
                    </Col>
                  </>
                )}
              </>
            );
          }}
        </Form.List>

        <Form.Item>
          <Button style={{ margin: 10 }} onClick={goBack}>
            Anterior
          </Button>
          <Button style={{ margin: 10 }} type="primary" htmlType="submit">
            Siguiente
          </Button>
        </Form.Item>
      </Form>
    </>
  );
};

export default BeneficiariosList;
